// @ts-nocheck
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import translate from "@insite/client-framework/Translate";
import { ButtonProps } from "@insite/mobius/Button";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import {
    Button,
    DataTableBody,
    DataTableCell,
    DataTableHead,
    DataTableHeader,
    DataTableRow,
} from "@insite/mobius/index";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import Pagination from "@store/MobiusOverrides/Pagination/Pagination";
import useBuyingList from "@store/Framework/Common/Hooks/useBuyingList/useBuyingList";
import useFetchWhenUpdateBuyingListParams from "@store/Framework/Common/Hooks/useBuyingList/useUpdateBuyingListPagination";
import useIsMobile from "@store/Framework/Common/Hooks/useIsMobile";
import useSearch from "@store/Framework/Common/Hooks/useSearch";
import EditItemListButton from "@store/Library/Components/Bunzl/Common/EditItemListButton";
import SearchBox from "@store/Library/Components/Bunzl/Common/SearchBox";
import BuyingListDetailsLink from "@store/Library/Components/BuyingList/BuyingListDetailsLink";
import ManageProductsModal from "@store/Library/Components/BuyingList/ManageProductsModal";
import { StyledCol } from "@store/Library/Components/Common/StyledCol";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import React, { useEffect } from "react";
import styled, { css } from "styled-components";

export const HR = styled.hr`
    width: 100%;
    border: 1px solid ${getColor("common.border")};
`;
const mobileWidths = [50, 50];
const desktopWidths = [30, 30, 20, 20];
const desktopBuyListHeaders = ["Title", "Description", "Products", "Actions"];
const mobileBuyListHeaders = ["Title", "Actions"];

const BuyingListDisplay = () => {
    const apiParams = useFetchWhenUpdateBuyingListParams();
    const isMobile = useIsMobile();
    const accountId = useAppSelector(state => state.pages.userSetup.userId) || "";
    const {
        getBuyingLists,
        buyingLists,
        getAssignedBuyingList,
        updateAssignedUsers,
        setBuyingListQuery,
        setPage,
        loading,
    } = useBuyingList();
    const [, setSearchTerm] = useSearch(setBuyingListQuery);
    const [openModalId, setOpenModalId] = React.useState<string | null>(null);
    const [assigned, setAssigned] = React.useState<{ [key: string]: string[] } | null>(null);
    const hasFetchedAssigned = React.useRef(false);
    const buyListHeaders = isMobile ? mobileBuyListHeaders : desktopBuyListHeaders;
    const setAssignedBuyingsLists = async () => {
        if (!buyingLists?.wishListCollection) {
            return;
        }
        const listIds = buyingLists?.wishListCollection?.map(buyingList => buyingList?.properties?.buyingListId) || [];

        const newBuyingLists = await Promise.all(listIds?.map(id => getAssignedBuyingList(id)));
        const assignedUsers = newBuyingLists?.map(list => {
            const users = list?.users?.map(user => user.id);
            return { [list.id]: users };
        });
        setAssigned(Object.assign({}, ...assignedUsers));
    };

    useEffect(() => {
        getBuyingLists(true);
    }, []);
    const handleAssign = async (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, buyingListId: string) => {
        event.preventDefault();
        const assignedUsers = assigned?.[buyingListId] || [];
        const isAssigned = assignedUsers?.includes(accountId);
        if (isAssigned) {
            const newAssign = assignedUsers?.filter(item => item !== accountId);
            await updateAssignedUsers(buyingListId, newAssign);
        } else {
            const newAssign = assigned ? [...assignedUsers, accountId] : [buyingListId];
            await updateAssignedUsers(buyingListId, newAssign);
        }
        setAssignedBuyingsLists();
    };
    const totalPages = buyingLists?.pagination?.numberOfPages || 1;
    if (buyingLists && !assigned && !hasFetchedAssigned.current) {
        hasFetchedAssigned.current = true;
        setAssignedBuyingsLists();
    }
    if (!assigned) {
        return null;
    }
    interface BuyingListDisplayStyles {
        gridItemColumn: GridItemProps;
        horizontalLineGridItem: GridItemProps;
        headerTypography: TypographyProps;
        subHeaderTypography: TypographyProps;
        horizontallyCenteredGridItem: GridItemProps;
        dataTable: DataTableProps;
        dataTableHeader: DataTableHeaderProps;
        dataTableLinkCell: DataTableCellProps;
        dataTableDescriptionCell: DataTableCellProps;
        dataTableAssignCell: DataTableCellProps;
        slightlyRoundedMinWidthButton: ButtonProps;
    }
    const buyingListDisplayStyles: BuyingListDisplayStyles = {
        gridItemColumn: {
            css: css`
                display: flex;
                flex-direction: column;
            `,
            width: 12 as const,
        },
        headerTypography: {
            css: css`
                margin-bottom: 0;
            `,
            variant: "h3" as const,
        },
        subHeaderTypography: {
            variant: "h5" as const,
            css: css`
                color: ${getColor("text.dark")};
                font-weight: 400;
                font-size: 1.125rem;
            `,
        },
        horizontalLineGridItem: {
            width: 12 as const,
            css: css`
                margin: 0;
                padding: 0 1rem;
            `,
        },
        horizontallyCenteredGridItem: {
            width: 12 as const,
            css: css`
                justify-content: center;
            `,
        },
        dataTable: {
            css: css`
                table-layout: fixed;
                border-collapse: separate;
                border-spacing: 0 ${pxToRem(2)};
            `,
        },
        dataTableHeader: {
            css: css`
                background-color: ${getColor("common.checkboxPickerBackground")};
            `,
        },
        dataTableLinkCell: {
            typographyProps: {
                ellipsis: true,

                underline: true,
                css: css`
                    display: block;
                    width: 6rem;
                `,
            },
            css: css`
                font-size: 1rem;
                font-weight: 600;
                span {
                    text-overflow: ellipsis;
                    overflow: hidden;
                    white-space: nowrap;
                    max-width: 10rem;
                }
            `,
        },
        dataTableDescriptionCell: {
            typographyProps: {
                ellipsis: true,

                css: css`
                    width: 12rem;
                    display: block;
                `,
            },
        },
        dataTableAssignCell: {
            css: css`
                width: 100%;
                display: inline-flex;
                justify-content: center;
            `,
        },
        slightlyRoundedMinWidthButton: {
            css: css`
                min-width: 8rem;
                border-radius: 0.5rem;
            `,
        },
    };

    return (
        <>
            <GridItem {...buyingListDisplayStyles.gridItemColumn}>
                <Typography {...buyingListDisplayStyles.headerTypography}>{translate("Manage Buying List")}</Typography>
                <Typography {...buyingListDisplayStyles.subHeaderTypography}>
                    {translate("Assign or Unassign Buying Lists")}
                </Typography>
            </GridItem>
            <GridItem {...buyingListDisplayStyles.horizontalLineGridItem}>
                <HR />
            </GridItem>
            <SearchBox
                placeholder={translate("Filter by Buying List Name")}
                title={translate("Buying Lists")}
                onSearch={setSearchTerm}
                extendedStyles={{ gridItem: { width: [12, 12, 12, 9, 9] } }}
            />
            <GridItem {...buyingListDisplayStyles.horizontallyCenteredGridItem} width={12}>
                {buyingLists && "pagination" in buyingLists && totalPages > 1 && (
                    <Pagination
                        currentPage={buyingLists?.pagination?.currentPage || 1}
                        onChangePage={setPage}
                        resultsCount={buyingLists?.pagination?.totalItemCount || 0}
                        resultsPerPage={buyingLists?.pagination?.pageSize || 0}
                        onChangeResultsPerPage={() => {}}
                        resultsPerPageOptions={[]}
                    />
                )}
            </GridItem>
            <GridItem width={12}>
                <DataTable {...buyingListDisplayStyles.dataTable}>
                    {isMobile ? (
                        <colgroup>
                            {mobileWidths.map((width, index) => (
                                <StyledCol key={`width-${index.toString()}`} width={width} />
                            ))}
                        </colgroup>
                    ) : (
                        <colgroup>
                            {desktopWidths.map((width, index) => (
                                <StyledCol key={`width-${index.toString()}`} width={width} />
                            ))}
                        </colgroup>
                    )}

                    <DataTableHead>
                        {/* @ts-ignore */}
                        {buyListHeaders.map((header: string) => (
                            <DataTableHeader
                                key={header}
                                // inline styles because css is dynamic and injection is messier.
                                css={css`
                                    ${header === "Actions" && "text-align: center;"}
                                    background-color: ${getColor("common.checkboxPickerBackground")};
                                `}
                            >
                                {header}
                            </DataTableHeader>
                        ))}
                    </DataTableHead>
                    <DataTableBody
                        css={css`
                            width: 100%;
                        `}
                    >
                        {buyingLists?.wishListCollection?.map(list => {
                            const buyingListId = list?.properties?.buyingListId;
                            const isActive = assigned?.[buyingListId]?.includes(accountId);

                            return (
                                <DataTableRow
                                    key={list.id}
                                    css={css`
                                        transition: all 0.2s ease-in-out;
                                        border-radius: 0.5rem;
                                        outline: ${isActive
                                            ? `2px solid ${getColor("secondary")}`
                                            : "2px solid transparent"};
                                    `}
                                >
                                    <DataTableCell
                                        title={isMobile ? list.description : undefined}
                                        {...buyingListDisplayStyles.dataTableLinkCell}
                                    >
                                        <BuyingListDetailsLink id={list.id} name={list.name} />
                                    </DataTableCell>
                                    {!isMobile && (
                                        <>
                                            <DataTableCell {...buyingListDisplayStyles.dataTableDescriptionCell}>
                                                {list.description}
                                            </DataTableCell>

                                            <DataTableCell>
                                                <ManageProductsModal
                                                    buyingList={list}
                                                    isOpen={openModalId === list.id}
                                                    onClose={() => setOpenModalId(null)}
                                                />
                                                <EditItemListButton
                                                    items={Number(list.properties?.wishListLineCount)}
                                                    onClick={() => setOpenModalId(list.id)}
                                                />
                                            </DataTableCell>
                                        </>
                                    )}
                                    <DataTableCell {...buyingListDisplayStyles.dataTableAssignCell}>
                                        {isActive ? (
                                            <Button
                                                onClick={event => handleAssign(event, buyingListId)}
                                                variant="tertiary"
                                                {...buyingListDisplayStyles.slightlyRoundedMinWidthButton}
                                            >
                                                {translate("Unassign")}
                                            </Button>
                                        ) : (
                                            <Button
                                                onClick={event => handleAssign(event, buyingListId)}
                                                {...buyingListDisplayStyles.slightlyRoundedMinWidthButton}
                                            >
                                                {translate("Assign")}
                                            </Button>
                                        )}
                                    </DataTableCell>
                                </DataTableRow>
                            );
                        })}
                    </DataTableBody>
                </DataTable>
            </GridItem>
        </>
    );
};

export default BuyingListDisplay;
